import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'app-add-contact',
  imports: [CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    InputMaskModule
  ],
  templateUrl: './add-contact.component.html',
  styleUrl: './add-contact.component.scss'
})
export class AddContactComponent implements OnInit {
  @Input() fieldLabels: { [key: string]: string } = {};
  @Input() fieldVisibility: { [key: string]: boolean } = {};
  @Input() fieldPlaceholders: { [key: string]: string } = {};
  @Input() sourceComponent: string = '';
  
  @Input() titles: any[] = [];
  @Input() accountNames: any[] = [];
  @Input() statuses: any[] = [];

  contactForm!: FormGroup;
  isBarNumberDisabled: boolean = false;
  isEditModeEnabled: boolean = false;
  isAccountNameDisabled: boolean = false; // Add this property
  selectedRowData: any = null;
  barNumber: string = '';
  hiddenId: boolean = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.contactForm = this.fb.group({
      accountName: ['', Validators.required],
      name: ['', Validators.required],
      title: ['', Validators.required],
      email: ['', [Validators.email, Validators.required]],
      phone: ['', this.getPhoneValidators()],
      extension: [''],
      barNumber: [''],
      status: [''],
      id: ['']
    });

    // this.setupInitialValidation();
  }

  // Method to disable account name field
  disableAccountNameField(): void {
    this.isAccountNameDisabled = true;
    this.contactForm.setValue({ ...this.contactForm.value });

    const accountNameControl = this.contactForm.get('accountName');
    
    if (accountNameControl) {
      accountNameControl.disable();
    }
    
    this.cdr.detectChanges();
  }

  // Method to enable account name field (for new contact creation)
  enableAccountNameField(): void {
    this.isAccountNameDisabled = false;
    const accountNameControl = this.contactForm.get('accountName');
    
    if (accountNameControl) {
      accountNameControl.enable();
    }
    
    this.cdr.detectChanges();
  }

  // Enhanced setFormData method
  setFormData(data: any): void {
    
    if (!this.contactForm) {
      console.error('Contact form is not initialized');
      return;
    }

    // Set form values
    this.contactForm.patchValue({
      accountName: data.accountName || data.clientName,
      name: data.name || '',
      title: data.title || '',
      email: data.email === '--' ? '' : data.email || '',
      phone: data.phone || '',
      extension: data.extension || '',
      barNumber: data.barNumber === '--' ? '' : data.barNumber || '',
      status: data.status || '',
      id: data.id || ''
    });

    this.barNumber = this.contactForm.get('barNumber')?.value;
    this.selectedRowData = this.contactForm.value;

    // Update field states based on loaded data
    if (data.title) {
      const titleValue = typeof data.title === 'object' ? data.title.value : data.title;
      this.isBarNumberDisabled = titleValue.toLowerCase() !== 'attorney';
    }
    
    // Re-setup validation after loading data
    this.updateBarNumberField();
    // this.updatePhoneField();
    
    // Mark form as dirty to show that it has been modified
    this.contactForm.markAsDirty();
  }

  // Enhanced resetForm method
  resetForm(): void {
    this.contactForm.reset();
    this.isBarNumberDisabled = false;
    this.isEditModeEnabled = false;
    this.isAccountNameDisabled = false; // Reset account name disabled state
    
    // Re-enable account name field when resetting
    this.enableAccountNameField();
    
    // Reset to initial state
    this.contactForm.patchValue({
      accountName: '',
      name: '',
      title: '',
      email: '',
      phone: '',
      extension: '',
      barNumber: '',
      status: '',
      id: ''
    });

    // Reset validators to initial state
    // this.setupInitialValidation();
  }

  // Update getFormData to handle disabled fields
  getFormData(): any {
    // Mark all fields as touched to show validation errors
    this.contactForm.markAllAsTouched();
    
    // For disabled fields, we need to get their values differently
    const formValue = { ...this.contactForm.value };
    
    // If account name is disabled, get its value from the raw form value
    if (this.isAccountNameDisabled) {
      const accountNameControl = this.contactForm.get('accountName');
      if (accountNameControl) {
        formValue.accountName = accountNameControl.getRawValue();
      }
    }
    
    if (!this.contactForm.valid) {
      return null;
    }

    formValue.status = formValue.status ? formValue.status.code : 'active';
    
    return formValue;
  }

  isError(controlName: string): boolean {
    const control = this.contactForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  isValid(): boolean {
    return this.contactForm.valid;
  }

  markTouched(): void {
    this.contactForm.markAllAsTouched();
  }

  onCancel(): void {
    this.contactForm.reset();
  }

  onTitleChange(event: any): void {
    const selectedTitle = event.value ? event.value.value : '';
    if (selectedTitle.toLowerCase() === 'attorney') {
      this.isBarNumberDisabled = false;
    } else {
      this.isBarNumberDisabled = true;
    }
    this.updateBarNumberField();
    this.cdr.detectChanges();
  }

  updateBarNumberField(): void {
    const barNumberControl = this.contactForm.get('barNumber');
    // Preserve current form values including disabled fields
    const currentValues = this.contactForm.getRawValue();

    if (this.isBarNumberDisabled) {
      barNumberControl?.setValue('');
      barNumberControl?.disable();
      barNumberControl?.setValidators(null);
      barNumberControl?.updateValueAndValidity();
    } else {
      if (this.isEditModeEnabled) {
        barNumberControl?.setValue(this.barNumber);
      } else {
        barNumberControl?.setValue('');
      }
      barNumberControl?.enable();
      barNumberControl?.setValidators(Validators.required);
      barNumberControl?.updateValueAndValidity();
    }
    
    // If account name was disabled, ensure it stays with its value
    if (this.isAccountNameDisabled) {
      const accountNameControl = this.contactForm.get('accountName');
      if (accountNameControl && currentValues.accountName) {
        accountNameControl.setValue(currentValues.accountName);
      }
    }
  }

  getPhoneValidators(): any {
    if (this.sourceComponent === 'account-contacts') {
      return [Validators.required];
    } else {
      return [];
    }
  }
}